/**
 * EntityUpsertForm – testable form-tab content for entity create/edit.
 *
 * Supports unindexed fields: when value equals nonIndexedStubValue and onRequestAttributeValue
 * is provided, shows "Not loaded*" + Load; on Load the delegate is called and parent updates formData.
 * For JSON-type attributes, uses a JSON editor when the value is loaded.
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Group,
  Text,
  Alert,
  Loader,
  TextInput,
  Textarea,
  Select,
  Checkbox,
  ActionIcon,
  Tooltip,
  Anchor,
} from '@mantine/core';
import { IconInfoCircle, IconAlertTriangle, IconDownload } from '@tabler/icons-react';
import type { EntityUpsertFormProps, FormAttribute } from './EntityUpsertForm.types';
import { defaultFormKeyFromAttrName } from './EntityUpsertForm.types';
import { getEntityValueFromARef } from '../../types';
import { getAttributeName, getAttributeLabel } from '../../utils/schemaAttributeUtils';
import {
  getDynamicFieldRendererDescriptor,
  renderDynamicField,
  DeferredLoadPlaceholder,
  FieldLabelWithRequired,
  type DynamicFieldAttribute,
  type GetDynamicFieldRendererDescriptorInput,
  type DynamicFieldRuntime,
} from './renderDynamicField';
import { PossessiveConfigurationSection } from './PossessiveConfigurationSection';
import { splitSchemaAttributesByPossessiveGroup } from './entityUpsertFormUtils';
import { ScimAceEditor } from '../common/ScimAceEditor';
import { EntityFieldFilterInput } from '../common/EntityFieldFilterInput';
import { getAxiosInstance } from '../../services/axiosInstance';
import { createEntityRef, isA } from '../../types';
import {
  entityFieldMatchesFilter,
  entityFieldSectionHasMatch,
  countEntityFieldFilterMatches,
  ENTITY_FIELD_FILTER_DIMMED_CLASS,
} from '../../utils/entityFieldFilterUtils';

export { splitSchemaAttributesByPossessiveGroup };

/** Wraps a field with an inline validation warning (icon + tooltip). */
function FieldWithWarning({ warning, children }: { warning: string; children: React.ReactNode }): React.ReactElement {
  return (
    <Box mb="md">
      {children}
      <Group gap="xs" mt={4} align="flex-start">
        <Tooltip label={warning} withArrow position="top-start">
          <Box component="span" display="inline-flex" lh={1}>
            <IconAlertTriangle size={16} color="var(--mantine-color-yellow-6)" />
          </Box>
        </Tooltip>
        <Text size="xs" c="yellow.7">{warning}</Text>
      </Group>
    </Box>
  );
}

function isJsonType(attr: FormAttribute): boolean {
  const t = (attr.type ?? '').toString().toLowerCase();
  return t === 'json' || t === 'object' || t === 'complex';
}

/** Serialize value for JSON editor: object/array to string, else stringify. */
function toJsonEditorValue(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

/** Simple default field renderer: supports not-loaded stub + Load, and JSON editor for json type. */
function DefaultFieldRenderer({
  attr,
  value,
  onChange,
  disabled,
  nonIndexedStubValue,
  onRequestAttributeValue,
  loadingAttribute,
  entityType,
  formData,
}: {
  attr: FormAttribute;
  value: unknown;
  onChange: (value: unknown) => void;
  disabled?: boolean;
  nonIndexedStubValue?: string;
  onRequestAttributeValue?: (attributeName: string) => Promise<unknown | null>;
  loadingAttribute?: string | null;
  entityType: string | null | undefined;
  formData: Record<string, unknown>;
}): React.ReactElement {
  const name = getAttributeName(attr);
  const label = getAttributeLabel(attr);
  const isReadOnly = attr.readOnly === true;
  const isDisabled = disabled || isReadOnly;
  const fieldLabel = <FieldLabelWithRequired label={label} required={attr.required === true} readOnly={isDisabled} />;
  const type = (attr.type || 'string').toLowerCase();
  const isNotLoaded = nonIndexedStubValue != null && value === nonIndexedStubValue;
  const canLoad = isNotLoaded && !!onRequestAttributeValue && !isDisabled;
  const isLoading = loadingAttribute === name;
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isDownloadingFileData, setIsDownloadingFileData] = useState(false);
  const normalizedAttrName = (name ?? '').toString().trim().replace(/^\$\./, '');
  const isFileDataField = (entityType ?? '').toLowerCase() === 'file' && isA(createEntityRef(normalizedAttrName || name), 'Data');

  const base64ToBytes = (base64: string): Uint8Array => {
    const normalized = base64.replace(/\s+/g, '');
    const binaryString = window.atob(normalized);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) bytes[i] = binaryString.charCodeAt(i);
    return bytes;
  };

  const inferMimeTypeFromFilename = (filename: string): string => {
    const n = filename.toLowerCase();
    if (n.endsWith('.json')) return 'application/json';
    if (n.endsWith('.txt') || n.endsWith('.log')) return 'text/plain';
    if (n.endsWith('.csv')) return 'text/csv';
    if (n.endsWith('.pdf')) return 'application/pdf';
    if (n.endsWith('.png')) return 'image/png';
    if (n.endsWith('.jpg') || n.endsWith('.jpeg')) return 'image/jpeg';
    if (n.endsWith('.gif')) return 'image/gif';
    if (n.endsWith('.zip')) return 'application/zip';
    return 'application/octet-stream';
  };

  const bytesToBlobPart = (bytes: Uint8Array): ArrayBuffer =>
    bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;

  const handleLoad = async () => {
    if (!onRequestAttributeValue) return;
    setLoadError(null);
    try {
      const result = await onRequestAttributeValue(name);
      onChange(result ?? '');
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load');
    }
  };

  if (canLoad) {
    // assuming nothing unindexed is required, nor readOnly
    return (
      <Box key={name} mb="md">
        <DeferredLoadPlaceholder
          fieldName={name}
          fieldLabel={label}
          loading={isLoading}
          onLoad={handleLoad}
          tooltipLabel="Sensitive, Large, or File value"
        />
        {loadError && (
          <Text size="xs" c="red" mt="xs">
            {loadError}
          </Text>
        )}
      </Box>
    );
  }

  if (isFileDataField && !isNotLoaded) {
    const base64 = typeof value === 'string' ? value.trim() : '';
    const filenameRaw = typeof formData?.DisplayName === 'string' && formData.DisplayName.trim()
      ? formData.DisplayName.trim()
      : 'download.bin';
    const filename = filenameRaw;
    const mimeType = inferMimeTypeFromFilename(filename);

    const canRender = base64 !== '';
    const handleDownload = () => {
      if (isDownloadingFileData) return;
      setIsDownloadingFileData(true);

      const downloadBlob = (blob: Blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      };

      const runFallback = async () => {
        // Upsert form fallback: we don't have the detail loader here; use whatever Data is loaded.
        const bytes = base64ToBytes(base64);
        downloadBlob(new Blob([bytesToBlobPart(bytes)], { type: mimeType }));
      };

      (async () => {
        let fallbackTimer: number | null = null;
        let completed = false;
        try {
          if (!canRender) return;

          const fileId = (formData as any)?.Id ?? (formData as any)?.id;
          const serverDownloadPromise = (async () => {
            if (!fileId) throw new Error('Missing file id');
            const axios = getAxiosInstance();
            const resp = await axios.get(`/entities/files/${encodeURIComponent(String(fileId))}/$download`, { responseType: 'blob' });
            return resp.data as Blob;
          })();

          fallbackTimer = window.setTimeout(() => {
            if (completed) return;
            void runFallback();
          }, 2000);

          const blob = await serverDownloadPromise;
          completed = true;
          if (fallbackTimer != null) window.clearTimeout(fallbackTimer);
          downloadBlob(blob);
        } catch {
          completed = true;
          if (fallbackTimer != null) window.clearTimeout(fallbackTimer);
          await runFallback();
        }
      })().finally(() => setIsDownloadingFileData(false));
    };
    return (
      <Box key={name} mb="md">
        <Text size="sm" fw={500} mb={4}>
          {fieldLabel}
        </Text>
        {canRender ? (
          <Group gap="xs" wrap="wrap">
            <Anchor
              component="button"
              onClick={handleDownload}
              aria-disabled={isDownloadingFileData}
              style={{ cursor: isDownloadingFileData ? 'not-allowed' : 'pointer', opacity: isDownloadingFileData ? 0.7 : 1 }}
            >
              <Group gap={6}>
                <IconDownload size={14} />
                <span>Download</span>
              </Group>
            </Anchor>
            <Text size="xs" c="dimmed">
              Client-side decode
            </Text>
          </Group>
        ) : (
          <Text size="sm" c="dimmed">
            —
          </Text>
        )}
      </Box>
    );
  }

  if (isJsonType(attr) && !isNotLoaded) {
    const editorValue = toJsonEditorValue(value);
    return (
      <Box key={name} mb="md">
        <Text size="sm" fw={500} mb={4}>
          {fieldLabel}
        </Text>
        <ScimAceEditor
          mode="json"
          value={editorValue}
          onChange={(str) => {
            try {
              const parsed = JSON.parse(str);
              onChange(parsed);
            } catch {
              onChange(str);
            }
          }}
          readOnly={isDisabled}
          height="200px"
        />
      </Box>
    );
  }

  const strValue = value != null && value !== '' ? String(value) : '';

  if (type === 'boolean') {
    return (
      <Checkbox
        key={name}
        label={label}
        checked={value === true || value === 'true'}
        onChange={(e) => onChange(e.currentTarget.checked)}
        disabled={isDisabled}
        mb="md"
      />
    );
  }

  // Don't use a dropdown for reference attributes – entity selection should use the reference browser (magnifying glass).
  const allowedValues = attr.allowedValues;
  if (Array.isArray(allowedValues) && allowedValues.length > 0 && !attr.isReference) {
    return (
      <Select
        key={name}
        label={fieldLabel}
        value={strValue || null}
        data={allowedValues.map((v) => ({ value: v, label: v }))}
        onChange={(v) => onChange(v ?? '')}
        disabled={isDisabled}
        mb="md"
      />
    );
  }

  const isMultiline =
    type === 'text' ||
    (typeof attr.type === 'string' && attr.type.toLowerCase() === 'text' && (label.toLowerCase().includes('description') || label.toLowerCase().includes('script')));
  if (isMultiline) {
    return (
      <Textarea
        key={name}
        label={fieldLabel}
        value={strValue}
        onChange={(e) => onChange(e.currentTarget.value)}
        onBlur={(e) => {
          const v = e.currentTarget.value.trim();
          if (v !== strValue) onChange(v);
        }}
        disabled={isDisabled}
        minRows={2}
        mb="md"
      />
    );
  }

  return (
    <TextInput
      key={name}
      label={fieldLabel}
      value={strValue}
      onChange={(e) => onChange(e.currentTarget.value)}
      onBlur={(e) => {
        const v = e.currentTarget.value.trim();
        if (v !== strValue) onChange(v);
      }}
      disabled={isDisabled}
      mb="md"
    />
  );
}

export function EntityUpsertForm({
  schemaAttributes,
  formData,
  onFieldChange,
  loading = false,
  entityLabel = 'entity',
  layout = 'default',
  possessiveGroupNames = [],
  renderField,
  showFieldDebug = false,
  onToggleFieldDebug,
  disabled = false,
  nonIndexedStubValue,
  onRequestAttributeValue,
  loadingAttribute = null,
  customLayout,
  entityType = null,
  formKeyFromAttrName = defaultFormKeyFromAttrName,
  legacySectionHeading,
  getFieldWarning,
}: EntityUpsertFormProps): React.ReactElement {
  const [fieldFilterQuery, setFieldFilterQuery] = useState('');

  const fieldFilterCounts = useMemo(() => {
    const entries = schemaAttributes.map((attr) => {
      const name = getAttributeName(attr);
      const value =
        (name ? getEntityValueFromARef(name, formData as Record<string, unknown>) : undefined) ?? '';
      return [
        { id: name, displayName: getAttributeLabel(attr), required: attr.required === true },
        value,
      ] as [{ id?: string; displayName?: string; required?: boolean }, unknown];
    });
    return countEntityFieldFilterMatches(entries, fieldFilterQuery);
  }, [schemaAttributes, formData, fieldFilterQuery]);

  const fieldFilterInput = (
    <EntityFieldFilterInput
      value={fieldFilterQuery}
      onChange={setFieldFilterQuery}
      matchCount={fieldFilterCounts.matching}
      totalCount={fieldFilterCounts.total}
    />
  );

  const wrapFilteredField = (
    attr: FormAttribute,
    value: unknown,
    content: React.ReactNode,
    key: string
  ): React.ReactNode => {
    const label = getAttributeLabel(attr);
    const name = getAttributeName(attr);
    const matches = entityFieldMatchesFilter(label, value, fieldFilterQuery, [name], {
      required: attr.required === true,
    });
    if (fieldFilterQuery.trim() && !matches) {
      return (
        <Box key={key} className={ENTITY_FIELD_FILTER_DIMMED_CLASS} mb="sm">
          {content}
        </Box>
      );
    }
    return <React.Fragment key={key}>{content}</React.Fragment>;
  };

  const attributeMatchesFilter = (attr: FormAttribute): boolean => {
    const name = getAttributeName(attr);
    const value =
      (name ? getEntityValueFromARef(name, formData as Record<string, unknown>) : undefined) ?? '';
    return entityFieldMatchesFilter(getAttributeLabel(attr), value, fieldFilterQuery, [name], {
      required: attr.required === true,
    });
  };

  const wrapWithFieldDebug = (fieldName: string, content: React.ReactNode): React.ReactNode => {
    if (!showFieldDebug) return content;
    const raw = formData[fieldName];
    const valuePreview =
      raw === undefined
        ? '(undefined)'
        : raw === null
          ? 'null'
          : typeof raw === 'string'
            ? (raw.length > 60 ? raw.slice(0, 60) + '…' : raw)
            : typeof raw === 'object'
              ? JSON.stringify(raw).slice(0, 60) + (JSON.stringify(raw).length > 60 ? '…' : '')
              : String(raw);
    const tooltipContent = `Field: ${fieldName}\nValue: ${valuePreview}`;
    return (
      <Box key={fieldName}>
        <Group gap="xs" align="flex-start">
          <Box flex={1} miw={0}>{content}</Box>
          <Box component="span" display="inline-flex" mt={6} title={tooltipContent}>
            <IconInfoCircle size={18} />
          </Box>
        </Group>
      </Box>
    );
  };

  const renderOneField = (attr: FormAttribute, index: number): React.ReactNode => {
    const name = getAttributeName(attr);
    const key = name || `attr-${index}`;
    // Form data loosely follows entity shape; use ref-based lookup so "$.Description" and "Description" match.
    const value = (name ? getEntityValueFromARef(name, formData as Record<string, unknown>) : undefined) ?? '';
    const handleChange = (v: unknown) => onFieldChange(name, v);
    const label = getAttributeLabel(attr);
    const fieldLabel = <FieldLabelWithRequired label={label} required={attr.required === true} readOnly={disabled} />;

    if (renderField) {
      const fieldContent = renderField(attr, value, handleChange);
      const warning = getFieldWarning?.(attr) ?? null;
      const wrapped = warning ? <FieldWithWarning warning={warning}>{fieldContent}</FieldWithWarning> : fieldContent;
      return wrapFilteredField(attr, value, wrapped, key);
    }

    if (nonIndexedStubValue != null && value === nonIndexedStubValue && onRequestAttributeValue) {
      return wrapFilteredField(
        attr,
        value,
        wrapWithFieldDebug(
          name || key,
          <DeferredLoadPlaceholder
            fieldName={name}
            fieldLabel={label}
            loading={loadingAttribute === name}
            onLoad={async () => {
              try {
                const v = await onRequestAttributeValue(name);
                handleChange(v ?? '');
              } catch (_) {
                /* caller may show error via loadingAttribute or separate state */
              }
            }}
            tooltipLabel="Sensitive, Large, or File value"
          />
        ),
        key
      );
    }

    const resolverInput: GetDynamicFieldRendererDescriptorInput = {
      attr: attr as DynamicFieldAttribute,
      entityType,
      formKeyFromAttrName,
      readOnly: disabled,
      loading,
    };
    const descriptor = getDynamicFieldRendererDescriptor(attr as DynamicFieldAttribute, resolverInput);

    if (descriptor.renderer !== 'DefaultText') {
      const runtime: DynamicFieldRuntime = {
        formData,
        onFieldChange,
        formKeyFromAttrName,
        entityType,
        readOnly: disabled,
        loading,
      };
      const content = renderDynamicField(attr as DynamicFieldAttribute, runtime);
      return wrapFilteredField(attr, value, wrapWithFieldDebug(name || key, content), key);
    }

    const content = (
      <DefaultFieldRenderer
        key={key}
        attr={attr}
        value={value}
        onChange={handleChange}
        disabled={disabled}
        nonIndexedStubValue={nonIndexedStubValue}
        onRequestAttributeValue={onRequestAttributeValue}
        loadingAttribute={loadingAttribute}
        entityType={entityType}
        formData={formData}
      />
    );
    return wrapFilteredField(attr, value, wrapWithFieldDebug(name || key, content), key);
  };

  if (loading) {
    return (
      <Group justify="center" align="center" py="xl">
        <Loader size="sm" />
        <Text size="sm" c="dimmed">
          Loading schema...
        </Text>
      </Group>
    );
  }

  if (!schemaAttributes || schemaAttributes.length === 0) {
    return (
      <Alert color="yellow" mb="md">
        No editable fields found for {entityLabel}. You may need to edit this entity after creation.
      </Alert>
    );
  }

  const debugToolbar =
    onToggleFieldDebug != null ? (
      <Group gap={4} align="center" mb={4} justify="flex-end">
        <Tooltip
          label={showFieldDebug ? 'Hide field debug info' : 'Show field debug info (raw values in tooltips)'}
          withArrow
        >
          <ActionIcon
            size="xs"
            variant={showFieldDebug ? 'light' : 'subtle'}
            onClick={onToggleFieldDebug}
            color={showFieldDebug ? 'blue' : 'gray'}
            aria-label={showFieldDebug ? 'Hide field debug' : 'Show field debug'}
            aria-pressed={showFieldDebug}
          >
            <IconInfoCircle size={14} />
          </ActionIcon>
        </Tooltip>
      </Group>
    ) : null;

  if (customLayout != null) {
    return (
      <Box>
        {fieldFilterInput}
        {debugToolbar}
        {customLayout}
      </Box>
    );
  }

  if (layout === 'possessive-group' && possessiveGroupNames.length > 0) {
    const { possessiveAttrs, otherAttrs } = splitSchemaAttributesByPossessiveGroup(
      schemaAttributes,
      possessiveGroupNames
    );
    const possessiveStartIndex = otherAttrs.length;
    const possessiveSectionEntries = possessiveAttrs.map((attr) => {
      const name = getAttributeName(attr);
      const value =
        (name ? getEntityValueFromARef(name, formData as Record<string, unknown>) : undefined) ?? '';
      return [
        { id: name, displayName: getAttributeLabel(attr), required: attr.required === true },
        value,
      ] as [{ id?: string; displayName?: string; required?: boolean }, unknown];
    });
    const showPossessiveSection = entityFieldSectionHasMatch(possessiveSectionEntries, fieldFilterQuery);

    return (
      <Box>
        {fieldFilterInput}
        {debugToolbar}
        {otherAttrs.map((attr, i) => renderOneField(attr, i))}
        {showPossessiveSection && (
          <PossessiveConfigurationSection
            attributes={possessiveAttrs}
            renderField={(attr, i) => renderOneField(attr, possessiveStartIndex + i)}
          />
        )}
      </Box>
    );
  }

  // Optional Legacy section: attributes whose name starts with "legacy" rendered after a heading
  const firstLegacyIndex =
    legacySectionHeading != null
      ? schemaAttributes.findIndex((attr) => getAttributeName(attr).toLowerCase().startsWith('legacy'))
      : -1;
  const mainAttrs = firstLegacyIndex === -1 ? schemaAttributes : schemaAttributes.slice(0, firstLegacyIndex);
  const legacyAttrs = firstLegacyIndex === -1 ? [] : schemaAttributes.slice(firstLegacyIndex);

  const legacyVisible =
    legacyAttrs.length > 0 &&
    (fieldFilterQuery.trim() === '' || legacyAttrs.some((attr) => attributeMatchesFilter(attr)));

  return (
    <Box>
      {fieldFilterInput}
      {debugToolbar}
      {mainAttrs.map((attr, index) => renderOneField(attr, index))}
      {legacyVisible && (
        <>
          {legacySectionHeading}
          {legacyAttrs.map((attr, index) => renderOneField(attr, mainAttrs.length + index))}
        </>
      )}
    </Box>
  );
}
